/* eslint-disable no-lonely-if */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { logo } from '../../../../configs/Icons';
import { useAuth } from '../../../Contexts/AuthProvider';
import { useInit } from '../../../Contexts/InitProvider';
import { useTemplate } from '../../../features/template/TemplateProvider';
import { ButtonPrimary } from '../../../utils/Button';
import FocusControl from '../../../utils/FocusControl';
import InputField, { InputPassword } from '../../../utils/InputField';
import PageTitle from '../../../utils/PageTitle';
import { mkRelativeUrl } from '../../../utils/URL';
import { handleUserLogInOpe } from '../UserControlOperation';

function ResendButton({ onClick }) {
    const [timeCount, setTimeCount] = useState(30);

    useEffect(() => {
        const interval = setInterval(() => {
            if (timeCount >= 1) {
                setTimeCount(timeCount - 1);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [timeCount]);

    return (
        <button
            type="button"
            onClick={() => onClick() + setTimeCount(60)}
            className={`h-10 text-sm w-[120px] uppercase text-primary-700 rounded-lg leading-10 transition-colors ${
                timeCount === 0
                    ? 'bg-secondary-200 text-secondary select-none'
                    : 'bg-primary-50 text-primary-700 pointer-events-none'
            }`}
        >
            {timeCount === 0 ? 'Resend Code' : `Resend in ${timeCount}s`}
        </button>
    );
}

function OtpFields({ formData, handleChange, isClicked, onSubmit, handleResendOpt, initInfo }) {
    return (
        <form
            onSubmit={onSubmit}
            className="w-[430px] shadow-[0px_4px_50px_0px_rgba(4,43,58,0.03)] mx-auto flex flex-col justify-between items-center gap-y-4 bg-white p-16 rounded-[30px]"
        >
            <div className="logo w-38 mb-3.5">
                <img src={initInfo?.adminLogo || logo.mainLogoW} alt="" />
            </div>
            <h1 className="w-full text-base font-medium text-primary-700 mb-3">
                {/* Write the OTP sent to your mail@noman.me */}
                Write the&nbsp;
                <span className="inline-block border-b border-secondary leading-8">
                    OTP for 2FA
                </span>
                &nbsp; verification
            </h1>
            <InputField
                name="OtpCode"
                formData={formData}
                onChange={handleChange}
                placeholder="Enter OTP"
                height="h-10"
            />
            <div className="w-full flex justify-between items-center">
                <ResendButton onClick={handleResendOpt} />
                <ButtonPrimary title="Verify" btnType="submit" loading={isClicked} />
            </div>
        </form>
    );
}

function LoginFields({ formData, handleChange, onSubmit, isLoading, initInfo }) {
    const inputUsername = document.querySelector('input[name="Username"]');

    useEffect(() => {
        inputUsername?.focus();

        return () => {
            inputUsername?.blur();
        };
    }, [inputUsername]);

    return (
        <form
            onSubmit={onSubmit}
            className="w-[430px] shadow-[0px_4px_50px_0px_rgba(4,43,58,0.03)] mx-auto flex flex-col justify-between items-center gap-y-4 bg-white p-8 lg:p-16 rounded-30"
        >
            <div className="logo w-38 mb-1 md:mb-3.5">
                <img src={initInfo?.adminLogo || logo.mainLogoW} alt="" />
            </div>

            <h1 className="w-full text-base font-medium text-primary-700 md:mb-3">
                <span className="inline-block border-b border-secondary leading-8">Login to</span>
                &nbsp;your Account
            </h1>

            <InputField
                name="Username"
                formData={formData}
                onChange={handleChange}
                placeholder="Enter your email or username"
            />

            <InputPassword
                name="Password"
                formData={formData}
                onChange={handleChange}
                placeholder="Enter your password"
            />

            <div className="input-form flex flex-row justify-between items-center w-full mb-3 mt-2">
                <Link
                    to={mkRelativeUrl('/forgot-password')}
                    className="inline-block underline text-primary-700 text-base font-normal"
                >
                    Forgot Password?
                </Link>
                <ButtonPrimary title="Login" btnType="submit" loading={isLoading} />
            </div>
        </form>
    );
}

function LoginPage() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const { setMessage } = useTemplate();
    const [isError, setIsError] = useState('');

    const [formData, setFormData] = useState({
        Username: '',
        Password: '',
        SecretKey: '',
        OtpCode: ''
    });
    const { isAuthenticated, authStateUpdated } = useAuth();

    const { initInfo } = useInit();

    const url = window.location?.search?.split('?url=')?.[1];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleLogin = (ev) => {
        setIsLoading(true);
        ev?.preventDefault();

        handleUserLogInOpe(formData)
            .then((data) => {
                if (data.referenceName === 'instOpenOtpInputWindow') {
                    if (step === 1) {
                        setStep(2);
                        setFormData((prev) => ({
                            ...prev,
                            SecretKey: data.data.secretKey
                        }));
                        setMessage({ type: 'success', text: data.title });
                    } else if (step === 2) {
                        setMessage({
                            type: data.status === 0 ? 'success' : 'error',
                            text: data.title
                        });
                    }
                } else {
                    if (data.status === 0) {
                        localStorage.setItem('token', data.data.token);
                        authStateUpdated();
                        if (url) {
                            window.location.href =
                                decodeURIComponent(url) || mkRelativeUrl('/dashboard');
                        }
                    } else {
                        setIsError(data.referenceName);
                        setMessage({ type: 'error', text: data.title });
                    }
                }

                // ## Any Response
                setIsLoading(false);
            })
            .catch((err) => {
                setMessage({ type: 'error', text: err.toString() });
                setIsLoading(false);
            });
    };

    const handleResendOtp = (ev) => {
        setIsLoading(true);
        ev?.preventDefault();

        handleUserLogInOpe({ Username: formData.Username, Password: formData.Password })
            .then((data) => {
                if (data.referenceName === 'instOpenOtpInputWindow') {
                    setFormData((prev) => ({
                        ...prev,
                        SecretKey: data.data.secretKey
                    }));

                    setMessage({
                        type: 'success',
                        text: data.title
                    });
                } else {
                    setMessage({
                        type: data.status === 0 ? 'success' : 'error',
                        text: data.title
                    });
                }

                setIsLoading(false);
            })
            .catch((err) => {
                setMessage({ type: 'error', text: err.toString() });
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (isAuthenticated() === true) {
            navigate(mkRelativeUrl('/dashboard'));
        }
    }, [isAuthenticated, navigate]);

    // --Focus Control UseEffect
    FocusControl(isError, setIsError, formData);
    PageTitle('Login');

    // --exit if user is not authenticated
    if (isAuthenticated() === true) {
        return null;
    }

    return (
        <div className="h-[calc(100vh_-_60px)] md:h-screen w-screen flex items-center justify-center bg-white-100 px-4 relative z-10">
            {initInfo?.adminBackground ? (
                <img
                    src={initInfo?.adminBackground}
                    alt="adminBackground"
                    className="absolute top-0 left-0 w-full h-full object-cover z-[-1]"
                />
            ) : null}

            {step === 1 && (
                <LoginFields
                    formData={formData}
                    handleChange={handleChange}
                    isLoading={isLoading}
                    onSubmit={handleLogin}
                    initInfo={initInfo}
                />
            )}
            {step === 2 && (
                <OtpFields
                    formData={formData}
                    handleChange={handleChange}
                    isLoading={isLoading}
                    onSubmit={handleLogin}
                    handleResendOpt={handleResendOtp}
                    initInfo={initInfo}
                />
            )}
        </div>
    );
}

export default LoginPage;
